/* eslint-disable */
import React, { useEffect, useRef, useState } from "react";
import {
  NavLink,
  Navbar as MantineNavBar,
  ScrollArea,
  Divider,
  Skeleton,
  Group,
  Text,
  Indicator,
} from "@mantine/core";
import { Link, useLocation } from "react-router-dom";
import { io, Socket } from "socket.io-client";
import { useGetProjects } from "api/projects/getProjects";
import UserProfile from "features/user/components/UserProfile";
import { Subtask, FolderOff, Bell, ChartTreemap } from "tabler-icons-react";
import styles from "./Navbar.module.css";
import { navConfig, NavItem } from "./navbardynamic";
import useChatSocket from "api/chat/chatSocket";
import { useGetUserPermissions } from "api/permission/getpermission";
import { useQueryClient } from "@tanstack/react-query";

type NavbarProps = { onClose: () => void; user: any };

export default function Navbar({ onClose, user }: NavbarProps) {
  const { pathname } = useLocation();
  const queryClient = useQueryClient();

  const buildLinkClass = (isActive?: boolean, extra?: string) =>
    `${styles.link} ${isActive ? styles.linkActive : ""} ${
      extra ? extra : ""
    }`.trim();

  const { data: permResp } = useGetUserPermissions(user._id);
  const permissions = permResp?.data?.permissions ?? {};

  const {
    data: projectData = {
      projects: [],
      createdIssuesLast7Days: [],
      completedIssuesLast7Days: [],
    },
    isLoading: projectsLoading,
    isSuccess: projectsSuccess,
  } = useGetProjects(user.org?._id, user._id);

  const [unreadCount, setUnreadCount] = useState(0);
  const [chatUnreadCount, setChatUnreadCount] = useState(0);
  const notifSocketRef = useRef<Socket | null>(null);

  const token =
    typeof window !== "undefined"
      ? localStorage.getItem("project_hub_token") ?? ""
      : "";

  const { socket: chatSocket, unreadMap } = useChatSocket({
    userId: user?._id,
    token,
  });

  useEffect(() => {
    if (!chatSocket) return;
    let totalUnread = 0;
    for (const count of Object.values(unreadMap)) {
      totalUnread += count;
    }
    setChatUnreadCount(totalUnread);
  }, [unreadMap, chatSocket]);

  useEffect(() => {
    if (!user?._id) return;
    const SOCKET_URL =
      process.env.REACT_APP_SOCKET_URL || window.location.origin;
    const s = io(SOCKET_URL, {
      auth: { token },
      query: { userId: user._id },
      transports: ["websocket"],
    });
    notifSocketRef.current = s;
    s.on("connect", () => {
      s.emit("getUnreadNotificationsCount");
    });
    s.on("UNREAD_NOTIFICATIONS_COUNT", (unreadCount: number) => {
      console.log("Received UNREAD_NOTIFICATIONS_COUNT:", unreadCount);
      if (typeof unreadCount === "number") setUnreadCount(unreadCount);
    });
    s.on("notification", (payload: any) => {
      if (typeof payload?.unreadCount === "number")
        setUnreadCount(payload.unreadCount);
      else setUnreadCount((prev) => prev + 1);
    });
    return () => {
      s.removeAllListeners();
      s.disconnect();
      notifSocketRef.current = null;
    };
  }, [user?._id, token]);

  const getNested = (obj: any, path: string) => {
    if (!obj || !path) return undefined;
    return path
      .split(".")
      .reduce(
        (acc: any, key: string) =>
          acc && acc[key] !== undefined ? acc[key] : undefined,
        obj
      );
  };

  const hasAccess = (item: NavItem) => {
    if (item.permissionKey) {
      if (Array.isArray(item.permissionKey)) {
        return item.permissionKey.some((p) => {
          const v = getNested(permissions, p);
          return (
            v === true ||
            (typeof v === "object" && Object.values(v).some(Boolean))
          );
        });
      } else {
        const v = getNested(permissions, item.permissionKey);
        return (
          v === true ||
          (typeof v === "object" && Object.values(v).some(Boolean))
        );
      }
    }

    if (item.allowedRoles && item.allowedRoles.includes(user.role || ""))
      return true;

    return false;
  };

  const renderNavItems = (items: NavItem[]) =>
    items
      .filter((item) => hasAccess(item))
      .map((item) => {
        // Projects Menu
        if (item.label === "Projects") {
          return (
            <NavLink
              key="projects"
              icon={item.icon}
              label={item.label}
              childrenOffset={24}
              className={styles.link}
            >
              <ScrollArea.Autosize maxHeight={270} scrollbarSize={10}>
                {projectData.projects.map((project: any) => {
                  const projTo = `/projects/${project._id}`;
                  const isActive = pathname === projTo;
                  return (
                    <NavLink
                      key={project._id}
                      icon={<Subtask />}
                      label={project.name}
                      component={Link}
                      to={projTo}
                      active={isActive}
                      onClick={onClose}
                      className={buildLinkClass(isActive)}
                    />
                  );
                })}
                {projectsLoading && (
                  <Group
                    spacing={12}
                    px={16}
                    py={10}
                    sx={(theme) => ({
                      borderLeft: `1px solid ${theme.colors.gray[3]}`,
                    })}
                  >
                    <Skeleton circle height={20} />
                    <Skeleton height={10} width={120} radius="xl" />
                  </Group>
                )}
                {projectsSuccess && projectData.projects.length === 0 && (
                  <Group align="center" spacing={10} py={5}>
                    <FolderOff size={18} color="#5C5F66" />
                    <Text align="center" size={14} color="dark.3">
                      No projects created
                    </Text>
                  </Group>
                )}
              </ScrollArea.Autosize>
            </NavLink>
          );
        }

        if (item.label === "Notifications") {
          const isActive = pathname === item.to;
          return (
            <NavLink
              key="notifications"
              icon={
                <Indicator
                  inline
                  label={unreadCount}
                  size={16}
                  disabled={unreadCount === 0}
                  color="red"
                >
                  <Bell size={24} />
                </Indicator>
              }
              label={item.label}
              component={Link}
              to={item.to}
              active={isActive}
              onClick={onClose}
              className={buildLinkClass(isActive)}
            />
          );
        }

        if (item.label === "Profile") {
          const profileTo = `/user-profile/${user._id}`;
          const isActive = pathname === profileTo;
          return (
            <NavLink
              key={item.to}
              icon={item.icon}
              label={item.label}
              component={Link}
              to={profileTo}
              active={isActive}
              onClick={onClose}
              className={buildLinkClass(isActive)}
            />
          );
        }

        if (item.children && item.children.length > 0) {
          const allowedChildren = item.children.filter((child) =>
            hasAccess(child)
          );
          if (allowedChildren.length === 0) return null;

          const anyChildActive = allowedChildren.some((child) => {
            const to =
              child.to === "/user-profile"
                ? `${child.to}/${user._id}`
                : child.to;
            return pathname === to;
          });

          return (
            <NavLink
              key={item.label}
              icon={
                item.label === "Connect" ? (
                  <Indicator
                    inline
                    label={chatUnreadCount}
                    size={16}
                    disabled={chatUnreadCount === 0}
                    color="red"
                  >
                    <ChartTreemap size={24} />
                  </Indicator>
                ) : (
                  item.icon
                )
              }
              label={item.label}
              childrenOffset={24}
              className={buildLinkClass(anyChildActive)}
            >
              <ScrollArea.Autosize maxHeight={200} scrollbarSize={10}>
                {allowedChildren.map((child) => {
                  const to =
                    child.to === "/user-profile"
                      ? `${child.to}/${user._id}`
                      : child.to;
                  const isActive = pathname === to;
                  return (
                    <NavLink
                      key={to}
                      icon={child.icon}
                      label={child.label}
                      component={Link}
                      to={to}
                      active={isActive}
                      sx={(theme) => ({
                        borderLeft: `1px solid ${theme.colors.gray[3]}`,
                      })}
                      onClick={onClose}
                      className={buildLinkClass(isActive, styles.linkChild)}
                    />
                  );
                })}
              </ScrollArea.Autosize>
            </NavLink>
          );
        }

        const isActive = pathname === item.to;
        return (
          <NavLink
            key={item.to}
            icon={item.icon}
            label={item.label}
            component={Link}
            to={item.to}
            active={isActive}
            onClick={onClose}
            className={buildLinkClass(isActive)}
          />
        );
      });

  return (
    <>
      <MantineNavBar.Section grow>
        {renderNavItems(navConfig)}
      </MantineNavBar.Section>
      <Divider my={15} />
      <MantineNavBar.Section>
        <UserProfile user={user} />
      </MantineNavBar.Section>
    </>
  );
}
